import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../Utils/axiosInstance";
import toast from "react-hot-toast";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { assets } from "../../Assets/assets";
import { getUser } from "../../Utils/auth";
import RechargeModal from "../../Components/UserSideComponents/Recharge/RechargeModal";
import TransactionDetailModal from "../../Components/UserSideComponents/Account/TransactionDetailModal";

// SKELETON COMPONENTS

const SkeletonBox = ({ className = "" }) => (
  <div className={`bg-skeletonLoaderColor animate-pulse rounded-xl ${className}`} />
);

const WalletSkeleton = () => (
  <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
    <div className="flex items-center gap-3 mb-6">
      <SkeletonBox className="w-9 h-9 rounded-xl" />
      <SkeletonBox className="h-7 w-36" />
    </div>
    <div className="flex flex-col lg:flex-row gap-5">
      {/* Sidebar */}
      <div className="w-full lg:w-80 flex-shrink-0 flex flex-col gap-4">
        <SkeletonBox className="h-52 w-full rounded-2xl" />
        <SkeletonBox className="h-11 rounded-xl" />
        <div className="grid grid-cols-2 gap-3">
          <SkeletonBox className="h-24 rounded-2xl" />
          <SkeletonBox className="h-24 rounded-2xl" />
        </div>
      </div>
      {/* Main */}
      <div className="flex-1 flex flex-col gap-5">
        <SkeletonBox className="h-64 w-full rounded-2xl" />
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex justify-between mb-4">
            <SkeletonBox className="h-5 w-32" />
            <SkeletonBox className="h-5 w-16" />
          </div>
          <div className="flex gap-3 mb-5">
            <SkeletonBox className="h-8 w-14 rounded-xl" />
            <SkeletonBox className="h-8 w-14 rounded-xl" />
            <SkeletonBox className="h-8 w-14 rounded-xl" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3 mb-3">
              <SkeletonBox className="h-10 w-10 rounded-xl flex-shrink-0" />
              <div className="flex-1">
                <SkeletonBox className="h-3.5 w-2/5 mb-2" />
                <SkeletonBox className="h-3 w-1/4" />
              </div>
              <div className="text-right space-y-2">
                <SkeletonBox className="h-4 w-20" />
                <SkeletonBox className="h-3 w-12 ml-auto" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ICONS (inline SVG — no extra deps)

const Icon = ({ d, size = 18, color = "currentColor", strokeWidth = 1.8 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round"
  >
    <path d={d} />
  </svg>
);
const ICONS = {
  plus: "M12 5v14M5 12h14",
  withdraw: "M17 8l4 4-4 4M3 12h18",
  credit: "M12 19V5M5 12l7-7 7 7",
  debit: "M12 5v14M5 12l7 7 7-7",
  calendar: "M3 6h18M8 2v4M16 2v4M3 6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H3z",
  wallet: "M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM16 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
  refresh: "M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15",
};

// CUSTOM CHART TOOLTIP

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-lg text-xs">
        <p className="text-secondaryTextColor mb-1 font-medium">{label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: p.color }} className="font-bold">
            {p.name === "credit" ? "↑" : "↓"} ₹{Number(p.value).toLocaleString("en-IN")}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// MAIN COMPONENT

const Wallet = () => {
  const navigate = useNavigate();
  const user = getUser();
  const userId = user?.id || user?._id;

  // ── Balance / chart / summary state (drives the sidebar + 7-day chart) ────
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]); // used ONLY for chart
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rechargeModalOpen, setRechargeModalOpen] = useState(false);

  // ── Transaction list state (drives the paginated table) ───────────────────
  const [historyTxns, setHistoryTxns] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txPagination, setTxPagination] = useState(null);
  const [txPage, setTxPage] = useState(1);
  const [activeTab, setActiveTab] = useState("all"); // all | credit | debit
  const [filters, setFilters] = useState({ startDate: "", endDate: "" });

  // ── Transaction detail modal state ─────────────────────────────────────────
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [txDetailModalOpen, setTxDetailModalOpen] = useState(false);

  /* ── fetch: balance + chart + summary (unchanged behavior) ── */
  const fetchWallet = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const [bal, txn, sum] = await Promise.all([
        axios.get(`/wallet/get-wallet-balance/${userId}`),
        axios.get(`/wallet/get-wallet-transactions/${userId}`),
        axios.get(`/wallet/get-wallet-summary/${userId}`),
      ]);
      setWallet(bal.data.data);
      setTransactions(txn.data.data.transactions);
      setSummary(sum.data.data.summary || {});
    } catch {
      toast.error("Wallet load failed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /* ── fetch: paginated transaction history for the list/table ── */
  const fetchTransactionHistory = async (id, page = 1, type = "all") => {
    setTxLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (type && type !== "all") params.append("type", type);
      const res = await axios.get(
        `/wallet/get-wallet-transactions/${id}?${params.toString()}`
      );
      if (res.data.success) {
        setHistoryTxns(res.data.data.transactions);
        setTxPagination(res.data.data.pagination);
      }
    } catch (err) {
      console.error("Failed to fetch transaction history:", err);
      toast.error("Failed to load transactions");
    } finally {
      setTxLoading(false);
    }
  };

  const handleRechargeSuccess = () => {
    fetchWallet();
    setTxPage(1);
    fetchTransactionHistory(userId, 1, activeTab);
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setTxDetailModalOpen(true);
  };

  const handleCloseTxDetailModal = () => {
    setSelectedTransaction(null);
    setTxDetailModalOpen(false);
  };

  // Initial balance/chart load
  useEffect(() => {
    fetchWallet();
    // const id = setInterval(() => fetchWallet(), 10000);
    // return () => clearInterval(id);
  }, []);

  // Paginated transaction history — refetch when userId, page, or type tab changes
  useEffect(() => {
    if (userId) {
      fetchTransactionHistory(userId, txPage, activeTab);
    }
  }, [userId, txPage, activeTab]);

  /* ── derived: date-range filtering applied on top of the current page ── */
  const filteredTxns = historyTxns.filter((t) => {
    const start = filters.startDate ? new Date(filters.startDate) : null;
    const end = filters.endDate ? new Date(filters.endDate) : null;
    const date = new Date(t.createdAt);
    const dateOk = (!start || date >= start) && (!end || date <= end);
    return dateOk;
  });

  /* 7-day area chart — still built from the full `transactions` fetched in fetchWallet */
  const chartData = (() => {
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return { label: d.toLocaleDateString("en-IN", { weekday: "short" }), credit: 0, debit: 0, date: d };
    });
    transactions.forEach((t) => {
      const td = new Date(t.createdAt);
      days.forEach((d) => {
        if (td.toDateString() === d.date.toDateString()) {
          t.type === "credit" ? (d.credit += t.amount) : (d.debit += t.amount);
        }
      });
    });
    return days.map(({ label, credit, debit }) => ({ label, credit, debit }));
  })();

  const totalCredit = summary?.credit?.totalAmount || 0;
  const totalDebit = summary?.debit?.totalAmount || 0;

  if (loading) return <WalletSkeleton />;

  /* ════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">

      {/* ── PAGE HEADER ── */}
      <div className="flex items-center justify-between mb-5">
        <div className="">
          <h1 className="text-xl flex items-center gap-2 sm:text-2xl font-bold text-primaryColor mb-1">
            <Icon d={ICONS.wallet} size={18} color="#772037" /> My Wallet
          </h1>
          <p className="text-sm text-gray-500">
            Manage your wallet and view transaction history
          </p>
        </div>
        <button
          onClick={() => {
            fetchWallet(true);
            fetchTransactionHistory(userId, txPage, activeTab);
          }}
          disabled={refreshing}
          className="flex items-center gap-2 text-xs font-semibold text-secondaryTextColor
            bg-white border border-gray-200 px-3 py-2 rounded-xl
            hover:border-primaryColor hover:text-primaryColor
            transition-all duration-200 disabled:opacity-60 disabled:pointer-events-none"
        >
          <span className={refreshing ? "animate-spin" : ""}>
            <Icon d={ICONS.refresh} size={13} color="currentColor" />
          </span>
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* ── LAYOUT ── */}
      <div className="flex flex-col lg:flex-row gap-5">

        {/* ═══ SIDEBAR ═══ */}
        <aside className="w-full lg:w-80 xl:w-[340px] flex-shrink-0 flex flex-col gap-4 lg:sticky lg:top-16 lg:self-start">

          {/* BALANCE CARD */}
          <div className="relative shineEffect rounded-2xl p-6 overflow-hidden text-white shadow-lg">
            {/* bg layers */}
            <div className="absolute inset-0 bg-gradient-to-br from-primaryColor via-subPrimaryColor to-[#5a1828]" />
            <div className="absolute -bottom-12 -right-12 w-52 h-52 rounded-full bg-white/5 blur-3xl pointer-events-none" />
            <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-primaryFadedColor/15 blur-2xl pointer-events-none" />
            {/* decorative circles */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full border border-white/10 translate-x-10 -translate-y-10 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full border border-white/10 -translate-x-6 translate-y-6 pointer-events-none" />

            <div className="relative z-10">
              {/* top row */}
              <div className="flex items-center justify-between mb-7">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center">
                    <Icon d={ICONS.wallet} size={14} color="white" />
                  </div>
                  <span className="text-[11px] font-semibold tracking-widest text-white/65 uppercase">
                    Wallet
                  </span>
                </div>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border
                  ${wallet.status === "active"
                    ? "bg-green-500/20 border-green-400/30 text-green-300"
                    : "bg-red-500/20 border-red-400/30 text-red-300"}`}>
                  {wallet.status}
                </span>
              </div>

              {/* balance */}
              <p className="text-[11px] text-white/60 mb-1 tracking-wide font-medium">
                Available Balance
              </p>
              <h2 className="text-[2.2rem] font-bold tracking-tight leading-none">
                ₹{Math.round(wallet.availableBalance).toLocaleString("en-IN")}
              </h2>

              {/* details */}
              <div className="mt-6 pt-4 border-t border-white/15 grid grid-cols-4 gap-2">
                <div>
                  <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">Total</p>
                  <p className="text-xs font-semibold">₹{Math.round(wallet.availableBalance).toLocaleString("en-IN")}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">Reserved</p>
                  <p className="text-xs font-semibold">₹{Number(wallet.reservedAmount).toLocaleString("en-IN")}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">Daily Limit</p>
                  <p className="text-xs font-semibold">₹{Number(wallet.dailyRechargeLimit).toLocaleString("en-IN")}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">Status</p>
                  <p className={`text-xs font-semibold capitalize ${wallet.status === "active" ? "text-green-300" : "text-red-300"}`}>
                    {wallet.status}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <button
            onClick={() => setRechargeModalOpen(true)}
            className="flex items-center justify-center gap-2
                bg-primaryBtnColor hover:bg-primaryBtnHoverColor
                text-white font-semibold text-sm py-3 rounded-xl
                transition-all duration-200 shadow-sm active:scale-95
                hover:shadow-md hover:shadow-primaryColor/20"
          >
            <Icon d={ICONS.plus} size={15} color="white" strokeWidth={2.5} />
            Add Money
          </button>

          {user?.role === "supplier" ? (
            <button
              onClick={() => navigate("/supplier-penalties")}
              className="flex items-center justify-center gap-2
                  bg-amber-50 hover:bg-amber-100 border border-amber-200
                  text-amber-800 font-bold text-xs py-2.5 rounded-xl
                  transition-all duration-200 shadow-2xs"
            >
              <span className="text-amber-600 font-bold text-sm">⚠️</span>
              Supplier Penalties & Deductions
            </button>
          ) : (
            <button
              onClick={() => navigate("/buyer-penalties")}
              className="flex items-center justify-center gap-2
                  bg-amber-50 hover:bg-amber-100 border border-amber-200
                  text-amber-800 font-bold text-xs py-2.5 rounded-xl
                  transition-all duration-200 shadow-2xs"
            >
              <span className="text-amber-600 font-bold text-sm">⚠️</span>
              Buyer Penalties & Dues
            </button>
          )}

          {/* STAT CARDS */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
              <div className="w-8 h-8 rounded-xl bg-green-50 flex items-center justify-center mb-3">
                <Icon d={ICONS.credit} size={15} color="#16a34a" strokeWidth={2} />
              </div>
              <p className="text-[10px] text-secondaryTextColor uppercase tracking-widest mb-1 font-semibold">
                Total Credit
              </p>
              <p className="text-base font-bold text-green-600 leading-tight">
                ₹{Number(totalCredit).toLocaleString("en-IN")}
              </p>
              {summary?.credit?.count != null && (
                <p className="text-[11px] text-secondaryTextColor mt-1">
                  {summary.credit.count} transactions
                </p>
              )}
            </div>
            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
              <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center mb-3">
                <Icon d={ICONS.debit} size={15} color="#dc2626" strokeWidth={2} />
              </div>
              <p className="text-[10px] text-secondaryTextColor uppercase tracking-widest mb-1 font-semibold">
                Total Debit
              </p>
              <p className="text-base font-bold text-red-500 leading-tight">
                ₹{Number(totalDebit).toLocaleString("en-IN")}
              </p>
              {summary?.debit?.count != null && (
                <p className="text-[11px] text-secondaryTextColor mt-1">
                  {summary.debit.count} transactions
                </p>
              )}
            </div>
          </div>
        </aside>

        {/* ═══ MAIN ═══ */}
        <main className="flex-1 min-w-0 flex flex-col gap-5">

          {/* CHART */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-primaryTextColor text-base">7-Day Activity</h3>
              <div className="flex items-center gap-4 text-xs text-secondaryTextColor">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
                  Credit
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" />
                  Debit
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={190}>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="gCredit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gDebit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#dc2626" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#6E6E6E", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#6E6E6E", fontSize: 10 }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="credit" stroke="#16a34a" strokeWidth={2} fill="url(#gCredit)" dot={false} />
                <Area type="monotone" dataKey="debit" stroke="#dc2626" strokeWidth={2} fill="url(#gDebit)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* TRANSACTIONS */}
          <div className="bg-white max-h-screen overflow-y-auto rounded-2xl p-5 mb-16 sm:mb-0 border border-gray-100 shadow-sm">
            {/* header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-primaryTextColor text-base">Transactions</h3>
              <span className="text-xs text-secondaryTextColor font-medium bg-gray-100 px-2.5 py-1 rounded-lg">
                {txPagination?.totalTransactions ?? filteredTxns.length} result
                {(txPagination?.totalTransactions ?? filteredTxns.length) !== 1 ? "s" : ""}
              </span>
            </div>

            {/* FILTERS */}
            <div className="flex flex-col sticky -top-5 bg-white z-10 sm:flex-row gap-3 mb-5">
              {/* Tab toggle */}
              <div className="flex bg-gray-100 rounded-xl p-1 gap-1 flex-shrink-0">
                {["all", "credit", "debit"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab);
                      setTxPage(1);
                    }}
                    className={`flex-1 sm:flex-none px-4 py-1.5 rounded-lg text-xs font-semibold capitalize
                      transition-all duration-200
                      ${activeTab === tab
                        ? "bg-white text-primaryColor shadow-sm"
                        : "text-secondaryTextColor hover:text-primaryTextColor"}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Date range */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Icon d={ICONS.calendar} size={13} color="#6E6E6E" />
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="wallet-date flex-1 min-w-0 border border-gray-200 rounded-xl
                    px-2.5 py-1.5 text-xs text-primaryTextColor bg-white outline-none
                    focus:border-primaryColor transition-all duration-200"
                />
                <span className="text-xs text-secondaryTextColor flex-shrink-0">–</span>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="wallet-date flex-1 min-w-0 border border-gray-200 rounded-xl
                    px-2.5 py-1.5 text-xs text-primaryTextColor bg-white outline-none
                    focus:border-primaryColor transition-all duration-200"
                />
                {(filters.startDate || filters.endDate) && (
                  <button
                    onClick={() => setFilters({ startDate: "", endDate: "" })}
                    className="flex-shrink-0 text-xs font-semibold text-secondaryTextColor
                      hover:text-primaryColor transition-colors underline underline-offset-2"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* TRANSACTION LIST */}
            <div className="flex flex-col gap-2">
              {txLoading ? (
                [...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />
                ))
              ) : filteredTxns.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14 gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                    <Icon d={ICONS.wallet} size={24} color="#6E6E6E" />
                  </div>
                  <p className="text-secondaryTextColor text-sm font-semibold">No transactions found</p>
                  <p className="text-xs text-secondaryTextColor/70 text-center max-w-[200px]">
                    Try adjusting your filters or date range
                  </p>
                </div>
              ) : (
                filteredTxns.map((txn, i) => (
                  <div
                    key={txn._id}
                    onClick={() => handleTransactionClick(txn)}
                    className="txn-row flex items-center gap-3 p-3 rounded-xl border border-gray-100
                      hover:border-primaryColor/25 hover:bg-primaryColor/[0.025]
                      transition-all duration-150 cursor-pointer"
                    style={{ animationDelay: `${Math.min(i * 35, 350)}ms` }}
                  >
                    {/* Icon bubble */}
                    <div className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center
                      ${txn.type === "credit" ? "bg-green-50" : "bg-red-50"}`}>
                      <Icon
                        d={txn.type === "credit" ? ICONS.credit : ICONS.debit}
                        size={15}
                        color={txn.type === "credit" ? "#16a34a" : "#dc2626"}
                        strokeWidth={2.2}
                      />
                    </div>

                    {/* Category + date */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-primaryTextColor truncate">
                        {txn.category || txn.description || "Transaction"}
                      </p>
                      <p className="text-[11px] text-secondaryTextColor mt-0.5">
                        {new Date(txn.createdAt).toLocaleDateString("en-IN", {
                          day: "numeric", month: "short", year: "numeric",
                        })}
                      </p>
                    </div>

                    {/* Amount + badge */}
                    <div className="text-right flex-shrink-0">
                      <p className={`text-sm font-bold ${txn.type === "credit" ? "text-green-600" : "text-red-500"}`}>
                        {txn.type === "credit" ? "+" : "−"}₹{Number(txn.amount).toLocaleString("en-IN")}
                      </p>
                      <span className={`text-[9px] font-bold uppercase tracking-widest
                        px-2 py-0.5 rounded-full mt-1 inline-block
                        ${txn.type === "credit" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
                        {txn.type}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {txPagination && txPagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-secondaryTextColor">
                  Page {txPagination.currentPage} of {txPagination.totalPages}{" "}
                  · {txPagination.totalTransactions} total
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={txPage <= 1}
                    onClick={() => setTxPage((p) => p - 1)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition"
                  >
                    Prev
                  </button>
                  <button
                    disabled={!txPagination.hasMore}
                    onClick={() => setTxPage((p) => p + 1)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}

      <RechargeModal
        isOpen={rechargeModalOpen}
        onClose={() => setRechargeModalOpen(false)}
        wallet={wallet}
        user={user} // minimal user (you already have userId)
        onSuccess={handleRechargeSuccess}
      />

      <TransactionDetailModal
        isOpen={txDetailModalOpen}
        onClose={handleCloseTxDetailModal}
        transaction={selectedTransaction}
        wallet={wallet}
      />
    </div>
  );
};

export default Wallet;